import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

// Types
export interface MarketingLink {
    id: string;
    label: string;
    slug: string;
    signupCount: number;
    maxSignups: number; // 0 = unlimited
    isActive: boolean;
    createdAt: Timestamp;
    createdBy: string;
    badgeText: string;
    badgeColor: string;
    // Enhanced fields
    description?: string;
    campaign?: string;
    expiresAt?: Timestamp | null;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    clickCount?: number;
    lastSignupAt?: Timestamp | null;
}

export interface MarketingLinksConfig {
    links: MarketingLink[];
    updatedAt: Timestamp;
}

const MARKETING_DOC_PATH = 'system/marketingLinks';

class MarketingService {
    /**
     * Get all marketing links from Firestore
     */
    async getMarketingLinks(): Promise<MarketingLink[]> {
        try {
            const docRef = doc(db, MARKETING_DOC_PATH);
            const docSnap = await getDoc(docRef);

            if (!docSnap.exists()) {
                return [];
            }

            const data = docSnap.data() as MarketingLinksConfig;
            return data.links || [];
        } catch (error) {
            console.error('Error fetching marketing links:', error);
            throw new Error('Failed to fetch marketing links');
        }
    }

    /**
     * Get a single link by its slug
     */
    async getLinkBySlug(slug: string): Promise<MarketingLink | null> {
        try {
            const links = await this.getMarketingLinks();
            return links.find(link => link.slug === slug) || null;
        } catch (error) {
            console.error('Error fetching link by slug:', error);
            return null;
        }
    }

    /**
     * Create a new marketing link
     */
    async createLink(config: {
        label: string;
        slug: string;
        badgeText: string;
        badgeColor: string;
        maxSignups?: number;
        createdBy: string;
        description?: string;
        campaign?: string;
        expiresAt?: Date | null;
        utmSource?: string;
        utmMedium?: string;
        utmCampaign?: string;
    }): Promise<MarketingLink> {
        try {
            const links = await this.getMarketingLinks();

            // Check for duplicate slug
            if (links.some(l => l.slug === config.slug)) {
                throw new Error(`A link with slug "${config.slug}" already exists`);
            }

            const newLink: MarketingLink = {
                id: `ml_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
                label: config.label,
                slug: config.slug,
                signupCount: 0,
                maxSignups: config.maxSignups || 0,
                isActive: true,
                createdAt: Timestamp.now(),
                createdBy: config.createdBy,
                badgeText: config.badgeText,
                badgeColor: config.badgeColor,
                description: config.description || '',
                campaign: config.campaign || '',
                expiresAt: config.expiresAt ? Timestamp.fromDate(config.expiresAt) : null,
                utmSource: config.utmSource || '',
                utmMedium: config.utmMedium || '',
                utmCampaign: config.utmCampaign || '',
                clickCount: 0,
                lastSignupAt: null,
            };

            const updatedLinks = [...links, newLink];
            await this.saveLinks(updatedLinks);

            return newLink;
        } catch (error) {
            console.error('Error creating marketing link:', error);
            throw error;
        }
    }

    /**
     * Update an existing marketing link
     */
    async updateLink(id: string, updates: Partial<Omit<MarketingLink, 'id' | 'createdAt' | 'createdBy'>>): Promise<void> {
        try {
            const links = await this.getMarketingLinks();
            const index = links.findIndex(l => l.id === id);

            if (index === -1) {
                throw new Error('Marketing link not found');
            }

            // If slug is being changed, check for duplicates
            if (updates.slug && updates.slug !== links[index].slug) {
                if (links.some(l => l.slug === updates.slug && l.id !== id)) {
                    throw new Error(`A link with slug "${updates.slug}" already exists`);
                }
            }

            links[index] = { ...links[index], ...updates };
            await this.saveLinks(links);
        } catch (error) {
            console.error('Error updating marketing link:', error);
            throw error;
        }
    }

    /**
     * Delete a marketing link
     */
    async deleteLink(id: string): Promise<void> {
        try {
            const links = await this.getMarketingLinks();
            const updatedLinks = links.filter(l => l.id !== id);

            if (updatedLinks.length === links.length) {
                throw new Error('Marketing link not found');
            }

            await this.saveLinks(updatedLinks);
        } catch (error) {
            console.error('Error deleting marketing link:', error);
            throw error;
        }
    }

    /**
     * Toggle a link's active status
     */
    async toggleLinkActive(id: string): Promise<boolean> {
        try {
            const links = await this.getMarketingLinks();
            const link = links.find(l => l.id === id);

            if (!link) {
                throw new Error('Marketing link not found');
            }

            link.isActive = !link.isActive;
            await this.saveLinks(links);

            return link.isActive;
        } catch (error) {
            console.error('Error toggling link status:', error);
            throw error;
        }
    }

    /**
     * Duplicate a marketing link with a new slug
     */
    async duplicateLink(id: string, newSlug: string): Promise<MarketingLink> {
        try {
            const links = await this.getMarketingLinks();
            const original = links.find(l => l.id === id);

            if (!original) {
                throw new Error('Marketing link not found');
            }

            if (links.some(l => l.slug === newSlug)) {
                throw new Error(`A link with slug "${newSlug}" already exists`);
            }

            const duplicated: MarketingLink = {
                ...original,
                id: `ml_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
                slug: newSlug,
                label: `${original.label} (Copy)`,
                signupCount: 0,
                clickCount: 0,
                createdAt: Timestamp.now(),
                lastSignupAt: null,
            };

            const updatedLinks = [...links, duplicated];
            await this.saveLinks(updatedLinks);

            return duplicated;
        } catch (error) {
            console.error('Error duplicating marketing link:', error);
            throw error;
        }
    }

    /**
     * Record a signup from a marketing link.
     * Called during the signup flow to tag the user and increment the count.
     */
    async recordSignup(slug: string, userId: string): Promise<{ badgeText: string; badgeColor: string; label: string } | null> {
        try {
            const links = await this.getMarketingLinks();
            const link = links.find(l => l.slug === slug);

            if (!link || !link.isActive) {
                console.log(`Marketing link "${slug}" not found or inactive, skipping source tagging`);
                return null;
            }

            // Check expiry
            if (link.expiresAt && link.expiresAt.toDate() < new Date()) {
                console.log(`Marketing link "${slug}" has expired`);
                return null;
            }

            // Check max signups cap
            if (link.maxSignups > 0 && link.signupCount >= link.maxSignups) {
                console.log(`Marketing link "${slug}" has reached max signups (${link.maxSignups})`);
                return null;
            }

            // Increment signup count and update last signup time
            link.signupCount += 1;
            link.lastSignupAt = Timestamp.now();
            await this.saveLinks(links);

            // Tag the user doc with signup source
            const userRef = doc(db, 'users', userId);
            await setDoc(userRef, {
                signupSource: slug,
                signupSourceLabel: link.label,
                signupSourceBadge: link.badgeText,
                signupSourceBadgeColor: link.badgeColor,
                signupSourceCampaign: link.campaign || '',
            }, { merge: true });

            return {
                badgeText: link.badgeText,
                badgeColor: link.badgeColor,
                label: link.label,
            };
        } catch (error) {
            console.error('Error recording marketing signup:', error);
            return null;
        }
    }

    /**
     * Record a click on a marketing link
     */
    async recordClick(slug: string): Promise<void> {
        try {
            const links = await this.getMarketingLinks();
            const link = links.find(l => l.slug === slug);
            if (link) {
                link.clickCount = (link.clickCount || 0) + 1;
                await this.saveLinks(links);
            }
        } catch (error) {
            console.error('Error recording click:', error);
        }
    }

    /**
     * Save links array to Firestore
     */
    private async saveLinks(links: MarketingLink[]): Promise<void> {
        const docRef = doc(db, MARKETING_DOC_PATH);

        // Convert MarketingLink[] to plain objects for Firestore
        const plainLinks = links.map(link => ({
            id: link.id,
            label: link.label,
            slug: link.slug,
            signupCount: link.signupCount,
            maxSignups: link.maxSignups,
            isActive: link.isActive,
            createdAt: link.createdAt,
            createdBy: link.createdBy,
            badgeText: link.badgeText,
            badgeColor: link.badgeColor,
            description: link.description || '',
            campaign: link.campaign || '',
            expiresAt: link.expiresAt || null,
            utmSource: link.utmSource || '',
            utmMedium: link.utmMedium || '',
            utmCampaign: link.utmCampaign || '',
            clickCount: link.clickCount || 0,
            lastSignupAt: link.lastSignupAt || null,
        }));

        await setDoc(docRef, {
            links: plainLinks,
            updatedAt: Timestamp.now(),
        });
    }

    /**
     * Generate a shareable signup URL with optional UTM params
     */
    getSignupUrl(link: MarketingLink | { slug: string; utmSource?: string; utmMedium?: string; utmCampaign?: string }): string {
        // Use production domain; fall back to localhost for development
        const origin = window.location.hostname === 'localhost'
            ? window.location.origin
            : 'https://app.scubamail.io';
        const url = new URL(`${origin}/signup`);
        url.searchParams.set('src', link.slug);

        if (link.utmSource) url.searchParams.set('utm_source', link.utmSource);
        if (link.utmMedium) url.searchParams.set('utm_medium', link.utmMedium);
        if (link.utmCampaign) url.searchParams.set('utm_campaign', link.utmCampaign);

        return url.toString();
    }

    /**
     * Get unique campaign names from all links
     */
    async getCampaigns(): Promise<string[]> {
        const links = await this.getMarketingLinks();
        const campaigns = new Set(links.map(l => l.campaign).filter(Boolean));
        return Array.from(campaigns) as string[];
    }
}

export const marketingService = new MarketingService();
